import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, getCategoryLabel, getUnitLabel, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { MapPin, Package, User, CheckCircle, Truck, Hand, ThumbsUp, Flag, Leaf, Pencil, QrCode, ArrowRight, History } from "lucide-react";
import BookingModal from "../components/BookingModal";
import QrPanel from "../components/QrPanel";
import SaveButton from "../components/SaveButton";
import VisibilityPicker from "../components/VisibilityPicker";
import NextActionsPanel from "../components/NextActionsPanel";
import AttachToProject from "../components/machines/AttachToProject";
import MoveMaterialModal from "../components/MoveMaterialModal";
import { toast } from "sonner";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [listing, setListing] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [showBooking, setShowBooking] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [moves, setMoves] = useState([]);
  const [showMoves, setShowMoves] = useState(false);
  const [justSaved, setJustSaved] = useState(!!location.state?.justSaved);

  // Clear router state so a manual refresh doesn't keep showing the panel
  useEffect(() => {
    if (location.state?.justSaved) {
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/listings/${id}`)
      .then((r) => { if (!cancelled) setListing(r.data); })
      .catch(() => {
        if (cancelled) return;
        toast.error(t("detail.notFound"));
        navigate("/marketplace");
      });
    return () => { cancelled = true; };
  }, [id, navigate, t]);

  if (!listing) {
    return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;
  }

  const handleBookClick = () => {
    if (!user) {
      toast.error(t("detail.loginFirst"));
      return;
    }
    if (user.role !== "buyer") {
      toast.error(t("detail.switchBuyer"));
      navigate("/dashboard");
      return;
    }
    if (user.user_id === listing.seller_id) {
      toast.error(t("detail.cantBookOwn"));
      return;
    }
    setShowBooking(true);
  };

  const images = listing.image_paths?.length ? listing.image_paths : [null];
  const priceLabel = listing.price > 0 ? `${listing.price} ${t("price.currency")}` : t("detail.freeHandover");

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-8">
        <Link to="/marketplace" className="label-tech hover:text-[var(--ink)]">{t("detail.back")}</Link>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 mt-4 grid grid-cols-12 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
        <div className="col-span-12 lg:col-span-7 bg-[var(--bone)] p-6">
          <div className="aspect-[4/3] bg-[var(--surface-alt)] overflow-hidden">
            {images[activeImg] ? (
              <img src={fileUrl(images[activeImg])} alt={listing.title} className="w-full h-full object-cover" data-testid="listing-main-image" />
            ) : (
              <div className="w-full h-full grid place-items-center text-[var(--ink-500)] label-tech">{t("card.noImage")}</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-px bg-[var(--ink-200)]">
              {images.map((p, i) => (
                <button key={p || `empty-${i}`} onClick={() => setActiveImg(i)} className={`w-20 h-20 overflow-hidden bg-[var(--surface-alt)] ${i === activeImg ? "ring-2 ring-[var(--ink)] ring-offset-0" : ""}`} data-testid={`listing-thumb-${i}`}>
                  {p ? <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" /> : null}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5 bg-[var(--surface)] p-6 md:p-8">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="tag">{getCategoryLabel(listing.category, lang)}</span>
            <span className="tag tag-moss">{listing.quality}</span>
            {listing.pickup_available && <span className="tag tag-slate"><Hand size={12} /> {t("detail.pickup")}</span>}
            {listing.delivery_available && <span className="tag tag-slate"><Truck size={12} /> {t("detail.delivery")}</span>}
            {user?.organization_id && listing.organization_id && listing.organization_id !== user.organization_id && (
              <span className="tag bg-[var(--brick)] text-[var(--bone)]" data-testid="external-resource-tag">{t("vis.externalTag")}</span>
            )}
            {listing.visibility === "shared" && (
              <span className="tag bg-[var(--moss)] text-[var(--bone)]" data-testid="shared-tag">{t("vis.sharedTag")}</span>
            )}
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">{listing.title}</h1>

          <div className="mt-5 flex items-center gap-6 text-sm text-[var(--ink-700)]">
            <span className="inline-flex items-center gap-1"><MapPin size={14} /> {listing.location}</span>
            <span className="inline-flex items-center gap-1" data-testid="listing-quantity-display">
              <Package size={14} />
              {listing.quantity_remaining !== undefined && listing.quantity_remaining < listing.quantity ? (
                <>
                  <strong>{listing.quantity_remaining}</strong> / {listing.quantity} {getUnitLabel(listing.unit, lang)} {t("detail.remaining")}
                </>
              ) : (
                <>{listing.quantity} {getUnitLabel(listing.unit, lang)} {t("detail.available")}</>
              )}
            </span>
          </div>

          <div className="mt-6 p-5 border border-[var(--ink-200)] bg-[var(--bone)]">
            <div className="label-tech">{t("detail.price")}</div>
            <div className="font-display text-3xl mt-1">{priceLabel}</div>
          </div>

          {listing.co2e && listing.co2e.co2e_kg > 0 && (
            <div className="mt-4 p-4 border border-[var(--moss)] bg-[var(--moss)] text-[var(--bone)]" data-testid="co2e-badge">
              <div className="label-tech opacity-80 flex items-center gap-2"><Leaf size={12} /> {t("detail.co2eSaved")}</div>
              <div className="font-display text-2xl mt-1">~{listing.co2e.co2e_kg} kg CO2e</div>
              <div className="label-tech mt-1 opacity-80">{listing.co2e.unit_note || t("detail.co2eKalla")}</div>
            </div>
          )}

          {listing.co2e && listing.co2e.needs_weight && (
            <div className="mt-4 p-4 border border-[var(--clay)] bg-[var(--clay)]/10" data-testid="co2e-needs-weight">
              <div className="label-tech text-[var(--clay-hover)] flex items-center gap-2"><Leaf size={12} /> {t("detail.co2eMissingWeight")}</div>
              <div className="text-sm text-[var(--ink-700)] mt-2 leading-relaxed">{t("detail.co2eMissingWeightHelp")}</div>
            </div>
          )}

          <div className="mt-6">
            <div className="label-tech mb-2">{t("detail.description")}</div>
            <p className="text-[var(--ink-700)] whitespace-pre-line leading-relaxed">{listing.description}</p>
          </div>

          <div className="mt-6 pt-6 border-t border-[var(--ink-200)] flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--ink)] text-[var(--bone)] grid place-items-center"><User size={16} /></div>
            <div className="flex-1">
              <div className="label-tech">{t("detail.seller")}</div>
              <div className="font-display">{listing.seller_name}</div>
            </div>
            {listing.seller_rating && listing.seller_rating.total > 0 ? (
              <span className="tag tag-moss inline-flex items-center gap-1" data-testid="seller-rating-summary">
                <ThumbsUp size={12} /> {listing.seller_rating.up}/{listing.seller_rating.total}
              </span>
            ) : (
              <span className="label-tech" data-testid="seller-rating-none">{t("rating.none")}</span>
            )}
            <SaveButton listingId={listing.id} />
          </div>

          <button onClick={handleBookClick} className="btn-primary w-full justify-center mt-6 py-4 text-sm" data-testid="book-listing-btn">
            <CheckCircle size={16} /> {t("detail.bookBtn")}
          </button>

          {user && (user.user_id === listing.seller_id || user.role === "admin") && (
            <button
              onClick={() => navigate(`/listings/${listing.id}/edit`)}
              className="mt-3 w-full py-3 border border-[var(--ink-200)] bg-[var(--bone)] hover:bg-[var(--surface-alt)] text-sm font-mono-tech uppercase tracking-[0.1em] inline-flex items-center justify-center gap-2"
              data-testid="edit-listing-btn"
            >
              <Pencil size={14} /> {t("detail.edit")}
            </button>
          )}

          {user && (user.user_id === listing.seller_id || user.role === "admin") && justSaved && (
            <div className="mt-6">
              <NextActionsPanel
                resourceType="material"
                resourceId={listing.id}
                onShare={() => {
                  setJustSaved(false);
                  setTimeout(() => {
                    const el = document.querySelector('[data-testid="visibility-picker"]');
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }, 50);
                }}
              />
            </div>
          )}

          {user && (user.user_id === listing.seller_id || user.role === "admin") && (
            <div className="mt-6">
              <VisibilityPicker
                resourceType="material"
                resourceId={listing.id}
                currentVisibility={listing.visibility}
                currentSharedWith={listing.shared_with}
                currentPrice={listing.price}
                onSetPrice={async (newPrice) => {
                  // Re-fetch the latest listing first so we PUT a complete payload
                  // (ListingCreate requires all fields)
                  const fresh = (await api.get(`/listings/${id}`)).data;
                  const { id: _id, seller_id: _s, seller_name: _sn, status: _st,
                          lifecycle: _l, organization_id: _o, created_at: _c,
                          quantity_booked: _qb, quantity_remaining: _qr,
                          seller_rating: _sr, co2e: _co, ...payload } = fresh;
                  await api.put(`/listings/${id}`, { ...payload, price: Number(newPrice) });
                }}
                onChange={() => api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => {})}
              />
            </div>
          )}

          {user && (user.user_id === listing.seller_id || user.role === "admin") && (
            <div className="mt-6">
              <AttachToProject
                objectType="material"
                objectId={listing.id}
                currentProjectId={listing.project_id}
                onChange={() => api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => {})}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  className="btn-primary"
                  onClick={() => setShowMove(true)}
                  data-testid="listing-move-btn"
                >
                  <ArrowRight size={14} /> {t("move.openBtn")}
                </button>
                <button
                  className="btn-ghost"
                  onClick={async () => {
                    if (!showMoves) {
                      try {
                        const r = await api.get(`/listings/${id}/moves`);
                        setMoves(Array.isArray(r.data) ? r.data : []);
                      } catch { setMoves([]); }
                    }
                    setShowMoves((v) => !v);
                  }}
                  data-testid="listing-history-toggle-btn"
                >
                  <History size={14} /> {showMoves ? t("move.historyHide") : t("move.historyShow")}
                </button>
              </div>
              {showMoves && (
                <div className="mt-3 border border-[var(--ink-200)] bg-[var(--bone)] p-4" data-testid="move-history">
                  <div className="label-tech mb-3">{t("move.historyTitle")}</div>
                  {moves.length === 0 && (
                    <div className="label-tech text-[var(--ink-500)]">{t("move.historyEmpty")}</div>
                  )}
                  {moves.length > 0 && (
                    <ol className="space-y-3">
                      {moves.map((m) => (
                        <li key={m.id} className="text-sm" data-testid={`move-history-${m.id}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="tag tag-moss">{m.from_project_name || t("move.noProject")}</span>
                            <ArrowRight size={12} className="text-[var(--ink-500)]" />
                            <span className="tag tag-brick">{m.to_project_name || t("move.noProject")}</span>
                          </div>
                          <div className="label-tech text-[var(--ink-500)] mt-1">
                            {new Date(m.moved_at).toLocaleString()} · {m.moved_by_name || m.moved_by}
                          </div>
                          {m.note && <div className="text-xs text-[var(--ink-700)] mt-1">"{m.note}"</div>}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}

          {user && (user.user_id === listing.seller_id || user.role === "admin") && (
            <div className="mt-6">
              <button
                className="btn-ghost"
                onClick={() => setShowQr((v) => !v)}
                data-testid="listing-qr-btn"
              >
                <QrCode size={14} /> {showQr ? t("qr.hide") : t("qr.show")}
              </button>
              {showQr && (
                <div className="mt-4">
                  <QrPanel
                    defaultMode="detail"
                    resourceName={listing.title}
                    urls={{
                      detail: { url: `${window.location.origin}/listings/${listing.id}`, label: t("qr.modeDetail"), hint: t("qr.scanHint") },
                      quickbook: { url: `${window.location.origin}/listings/${listing.id}/quickbook`, label: t("qr.modeQuickBook"), hint: t("qr.scanHintQuickBookMaterial") },
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {user && (
            <button
              onClick={async () => {
                const reason = prompt(t("admin.reportPrompt"));
                if (!reason) return;
                try {
                  await api.post("/reports", { listing_id: listing.id, reason });
                  toast.success(t("admin.reportSent"));
                } catch {
                  toast.error("Error");
                }
              }}
              className="label-tech mt-4 hover:text-[var(--brick)] inline-flex items-center gap-1"
              data-testid="report-listing-btn"
            >
              <Flag size={12} /> {t("admin.reportBtn")}
            </button>
          )}
        </div>
      </div>

      {showBooking && <BookingModal listing={listing} onClose={() => setShowBooking(false)} />}
      {showMove && (
        <MoveMaterialModal
          listingIds={[listing.id]}
          currentProjectId={listing.project_id}
          onClose={() => setShowMove(false)}
          onMoved={() => api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => {})}
        />
      )}
    </Layout>
  );
}
